import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRightLeft,
  Eye,
  FileDown,
  FileText,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useRef, useState } from "react";

import { BajaEquipoModal } from "@/components/BajaEquipoModal";
import { TransferirEquipoModal } from "@/components/TransferirEquipoModal";
import { DescargandoOverlay } from "@/components/DescargandoOverlay";
import {
  actualizarEquipo,
  crearEquipo,
  darDeBajaEquipo,
  descargarFormatoBaja,
  descargarResponsiva,
  getBajasEquipo,
  getEmpleados,
  getEquipos,
  getMe,
  restaurarBajaEquipo,
  TIPOS_EQUIPO,
  transferirEquipo,
  type BajaEquipo,
  type Equipo,
  type EquipoInput,
  type TipoBaja,
} from "@/lib/api";
import {
  borrarEscaneada,
  getEscaneadaInfo,
  getEscaneadasEmails,
  subirEscaneada,
  verEscaneada,
} from "@/lib/escaneadas";

const FORM_VACIO = {
  tipo: TIPOS_EQUIPO[0] as string,
  marca: "",
  modelo: "",
  numero_serie: "",
  caracteristicas: "",
  incluye_cargador: false,
};

const fechaCorta = (iso: string) =>
  new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });

export function ResponsivasPage() {
  const qc = useQueryClient();

  const empleadosQ = useQuery({
    queryKey: ["empleados", "todas"],
    queryFn: () => getEmpleados("todas"),
  });
  const [empleado, setEmpleado] = useState("");

  const equiposQ = useQuery({
    queryKey: ["equipos", empleado],
    queryFn: () => getEquipos(empleado),
    enabled: !!empleado,
  });

  // Misma query (y caché) que App.tsx: solo nos interesa si es administrador.
  const meQ = useQuery({ queryKey: ["me"], queryFn: getMe, staleTime: Infinity, retry: false });
  const esAdmin = meQ.data?.es_admin ?? false;

  // Bajas del usuario seleccionado: todos ven las físicas (entrega del equipo);
  // el administrador ve además las lógicas. Se espera a saber el rol para no
  // pedir dos veces.
  const tipoBajas = esAdmin ? "todas" : "fisica";
  const bajasQ = useQuery({
    queryKey: ["bajas", empleado, tipoBajas],
    queryFn: () => getBajasEquipo(empleado, tipoBajas),
    enabled: !!empleado && meQ.isFetched,
    retry: false,
  });

  const escaneadaQ = useQuery({
    queryKey: ["escaneada", empleado],
    queryFn: () => getEscaneadaInfo(empleado),
    enabled: !!empleado,
  });
  // Quiénes ya tienen PDF firmado (🟢) y a quién le cambió el equipo después
  // de subirlo (🟡) — pinta la bolita del selector.
  const escaneadasQ = useQuery({
    queryKey: ["escaneadas-lista"],
    queryFn: getEscaneadasEmails,
  });
  const estadoPdf = escaneadasQ.data;
  const bolita = (email: string) =>
    !estadoPdf
      ? ""
      : estadoPdf.desactualizadas.has(email)
        ? "🟡 "
        : estadoPdf.conPdf.has(email)
          ? "🟢 "
          : "🔴 ";
  const pdfDesactualizado = !!empleado && !!estadoPdf?.desactualizadas.has(empleado);
  const fileRef = useRef<HTMLInputElement>(null);
  const [subiendo, setSubiendo] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...FORM_VACIO });
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [descargando, setDescargando] = useState<string | null>(null); // detalle del overlay
  const [bajaPendiente, setBajaPendiente] = useState<Equipo | null>(null);
  const [transferPendiente, setTransferPendiente] = useState<Equipo | null>(null);

  const set = (patch: Partial<typeof FORM_VACIO>) => setForm((f) => ({ ...f, ...patch }));

  const reset = () => {
    setEditingId(null);
    setForm({ ...FORM_VACIO });
    setError(null);
  };

  // Cualquier alta/edición/baja de equipo puede volver 🟡 la responsiva del usuario.
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["equipos"] });
    qc.invalidateQueries({ queryKey: ["escaneadas-lista"] });
  };
  const refreshBajas = () => {
    qc.invalidateQueries({ queryKey: ["bajas"] });
    qc.invalidateQueries({ queryKey: ["escaneadas-lista"] });
  };

  const crear = useMutation({
    mutationFn: (e: EquipoInput) => crearEquipo(e),
    onSuccess: () => {
      refresh();
      reset();
      setMsg("Equipo asignado.");
    },
    onError: (e: Error) => setError(e.message),
  });
  const actualizar = useMutation({
    mutationFn: ({ id, e }: { id: number; e: EquipoInput }) => actualizarEquipo(id, e),
    onSuccess: () => {
      refresh();
      reset();
      setMsg("Equipo actualizado.");
    },
    onError: (e: Error) => setError(e.message),
  });
  const darDeBaja = useMutation({
    mutationFn: ({ id, tipo, motivo }: { id: number; tipo: TipoBaja; motivo: string }) =>
      darDeBajaEquipo(id, tipo, motivo),
    onSuccess: (r) => {
      refresh();
      refreshBajas();
      setBajaPendiente(null);
      setMsg(
        r.tipo_baja === "fisica"
          ? "Baja física registrada. Ya puedes generar el formato PDF para firma en “Bajas de asignación”."
          : "Equipo quitado de la lista (baja lógica).",
      );
    },
    onError: (e: Error) => {
      setBajaPendiente(null);
      setError(e.message);
    },
  });
  const transferir = useMutation({
    mutationFn: ({ id, destino, motivo }: { id: number; destino: string; motivo: string }) =>
      transferirEquipo(id, destino, motivo),
    onSuccess: (nuevo) => {
      refresh();
      refreshBajas();
      setTransferPendiente(null);
      setMsg(
        `${nuevo.tipo} transferido a ${nuevo.empleado_nombre ?? nuevo.empleado_email}. Genera de nuevo la responsiva de ambos usuarios.`,
      );
    },
    onError: (e: Error) => {
      setTransferPendiente(null);
      setError(e.message);
    },
  });
  const restaurar = useMutation({
    mutationFn: (id: number) => restaurarBajaEquipo(id),
    onSuccess: () => {
      refresh();
      refreshBajas();
      setMsg("Equipo restaurado: vuelve a estar asignado al usuario.");
    },
    onError: (e: Error) => setError(e.message),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMsg(null);
    if (!empleado) {
      setError("Selecciona primero al usuario.");
      return;
    }
    const payload: EquipoInput = { empleado_email: empleado, ...form };
    if (editingId != null) actualizar.mutate({ id: editingId, e: payload });
    else crear.mutate(payload);
  };

  const startEdit = (row: Equipo) => {
    setEditingId(row.id);
    setForm({
      tipo: row.tipo,
      marca: row.marca,
      modelo: row.modelo,
      numero_serie: row.numero_serie,
      caracteristicas: row.caracteristicas,
      incluye_cargador: row.incluye_cargador,
    });
    setError(null);
    setMsg(null);
  };

  const generar = async () => {
    if (descargando) return;
    setError(null);
    setMsg(null);
    setDescargando("Estamos generando la responsiva en Word, espera un momento.");
    try {
      await descargarResponsiva(empleado);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDescargando(null);
    }
  };

  const generarFormatoBaja = async (b: BajaEquipo) => {
    if (descargando) return;
    setError(null);
    setMsg(null);
    setDescargando("Estamos generando el formato de baja de asignación (PDF), espera un momento.");
    try {
      await descargarFormatoBaja(b.id, `BAJA DE ASIGNACION EQUIPO - ${nombreSel} - ${b.tipo}.pdf`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDescargando(null);
    }
  };

  const onRestaurar = (b: BajaEquipo) => {
    if (
      !confirm(
        `¿Restaurar este ${b.tipo} a ${nombreSel}?\n\nVolverá a la lista de equipos asignados y se eliminará el registro de la baja.`,
      )
    )
      return;
    setError(null);
    setMsg(null);
    restaurar.mutate(b.id);
  };

  const onSubirPdf = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setMsg(null);
    setSubiendo(true);
    try {
      await subirEscaneada(empleado, file);
      qc.invalidateQueries({ queryKey: ["escaneada", empleado] });
      qc.invalidateQueries({ queryKey: ["escaneadas-lista"] });
      setMsg("PDF de la responsiva firmada guardado.");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubiendo(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onVerPdf = async () => {
    setError(null);
    try {
      await verEscaneada(empleado);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const onBorrarPdf = async () => {
    if (!confirm("¿Eliminar el PDF de la responsiva firmada?")) return;
    setError(null);
    setMsg(null);
    try {
      await borrarEscaneada(empleado);
      qc.invalidateQueries({ queryKey: ["escaneada", empleado] });
      qc.invalidateQueries({ queryKey: ["escaneadas-lista"] });
      setMsg("PDF eliminado.");
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const saving = crear.isPending || actualizar.isPending;
  const empleados = empleadosQ.data ?? [];
  const rows = equiposQ.data ?? [];
  const bajas = bajasQ.data ?? [];
  const seleccionado = empleados.find((e) => e.email === empleado);
  const nombreSel = seleccionado?.nombre ?? empleado.split("@")[0];

  return (
    <div className="space-y-5 max-w-5xl">
      <DescargandoOverlay visible={!!descargando} detalle={descargando ?? undefined} />
      {bajaPendiente && (
        <BajaEquipoModal
          equipo={bajaPendiente}
          nombre={nombreSel}
          pending={darDeBaja.isPending}
          onCancel={() => setBajaPendiente(null)}
          onConfirm={(tipo, motivo) => {
            setError(null);
            setMsg(null);
            darDeBaja.mutate({ id: bajaPendiente.id, tipo, motivo });
          }}
        />
      )}
      {transferPendiente && (
        <TransferirEquipoModal
          equipo={transferPendiente}
          nombre={nombreSel}
          empleados={empleados}
          pending={transferir.isPending}
          onCancel={() => setTransferPendiente(null)}
          onConfirm={(destino, motivo) => {
            setError(null);
            setMsg(null);
            transferir.mutate({ id: transferPendiente.id, destino, motivo });
          }}
        />
      )}
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Responsivas de equipo</h1>
        <p className="text-sm text-slate-500 mt-1">
          Selecciona al usuario, captura los equipos que tiene asignados y genera su
          responsiva de equipo de cómputo (Word). Al eliminar un equipo indica si la baja es
          lógica o física; las físicas quedan registradas abajo con su formato PDF para firma.{" "}
          <span className="whitespace-nowrap">
            🟢 = con PDF firmado cargado · 🟡 = cambió su equipo, falta actualizar la responsiva ·
            🔴 = sin PDF.
          </span>
        </p>
      </div>

      <div className="bg-white border rounded-lg p-4 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Usuario</span>
          <select
            className="mt-1 border rounded px-2 py-1.5 text-sm block min-w-[16rem]"
            value={empleado}
            onChange={(e) => {
              setEmpleado(e.target.value);
              reset();
              setMsg(null);
            }}
          >
            <option value="">— Selecciona —</option>
            {[...new Set(empleados.map((e) => e.empresa))].map((emp) => (
              <optgroup key={emp} label={emp}>
                {empleados
                  .filter((e) => e.empresa === emp)
                  .map((e) => (
                    <option key={e.email} value={e.email}>
                      {bolita(e.email)}
                      {e.nombre ?? e.email}
                    </option>
                  ))}
              </optgroup>
            ))}
          </select>
        </label>
        {seleccionado && (
          <div className="flex items-center gap-3 pb-1.5">
            <span
              className={`text-[11px] px-2 py-0.5 rounded-full ${
                seleccionado.empresa === "MPP ENERGIA"
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-slate-200 text-slate-700"
              }`}
              title="Empresa del usuario (propiedad del directorio)"
            >
              {seleccionado.empresa}
            </span>
            {rows.length > 0 && (
              <button
                type="button"
                disabled={!!descargando}
                onClick={generar}
                className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded px-4 py-2 text-sm hover:bg-emerald-700 disabled:opacity-50"
              >
                <FileText className="w-4 h-4" />
                {descargando ? "Generando…" : "Generar responsiva"}
              </button>
            )}
          </div>
        )}
        {seleccionado && (
          <div className="w-full border-t pt-3 mt-1 flex flex-wrap items-center gap-3">
            <span className="text-xs font-medium text-slate-600">
              Responsiva firmada (PDF):
            </span>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => onSubirPdf(e.target.files?.[0])}
            />
            {escaneadaQ.data?.existe ? (
              <>
                <span className="text-sm text-slate-700">
                  {escaneadaQ.data.nombre_archivo}
                  <span className="text-slate-400">
                    {" "}
                    · subido el {escaneadaQ.data.subido_en?.slice(0, 10)} por{" "}
                    {escaneadaQ.data.subido_por}
                  </span>
                </span>
                {pdfDesactualizado && (
                  <span
                    className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200"
                    title="Se agregó, editó o dio de baja equipo después de subir este PDF"
                  >
                    🟡 Por actualizar: el equipo cambió después de firmarla. Genera la responsiva
                    nueva y reemplaza el PDF.
                  </span>
                )}
                <button
                  type="button"
                  onClick={onVerPdf}
                  className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800"
                >
                  <Eye className="w-4 h-4" /> Ver
                </button>
                <button
                  type="button"
                  disabled={subiendo}
                  onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-50"
                >
                  <Upload className="w-4 h-4" /> {subiendo ? "Subiendo…" : "Reemplazar"}
                </button>
                <button
                  type="button"
                  onClick={onBorrarPdf}
                  className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-4 h-4" /> Eliminar
                </button>
              </>
            ) : (
              <>
                <span className="text-sm text-slate-400">Sin PDF cargado.</span>
                <button
                  type="button"
                  disabled={subiendo}
                  onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 rounded px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
                >
                  <Upload className="w-4 h-4" />
                  {subiendo ? "Subiendo…" : "Subir PDF escaneado"}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {empleado && (
        <form
          onSubmit={submit}
          className="bg-white border rounded-lg p-4 space-y-3"
        >
          <div className="text-sm font-medium text-slate-700">
            {editingId != null ? "Editar equipo" : `Asignar equipo a ${nombreSel}`}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Tipo de equipo</span>
              <select
                className="mt-1 border rounded px-2 py-1.5 text-sm block w-44"
                value={form.tipo}
                onChange={(e) => set({ tipo: e.target.value })}
              >
                {TIPOS_EQUIPO.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Marca</span>
              <input
                type="text"
                className="mt-1 border rounded px-2 py-1.5 text-sm block w-36"
                value={form.marca}
                maxLength={80}
                onChange={(e) => set({ marca: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Modelo</span>
              <input
                type="text"
                className="mt-1 border rounded px-2 py-1.5 text-sm block w-44"
                value={form.modelo}
                maxLength={120}
                onChange={(e) => set({ modelo: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">No. de serie</span>
              <input
                type="text"
                className="mt-1 border rounded px-2 py-1.5 text-sm block w-40"
                value={form.numero_serie}
                maxLength={80}
                onChange={(e) => set({ numero_serie: e.target.value })}
              />
            </label>
            <label className="block flex-1 min-w-[14rem]">
              <span className="text-xs font-medium text-slate-600">
                Características adicionales
              </span>
              <input
                type="text"
                className="mt-1 border rounded px-2 py-1.5 text-sm block w-full"
                value={form.caracteristicas}
                maxLength={300}
                onChange={(e) => set({ caracteristicas: e.target.value })}
              />
            </label>
            <label className="inline-flex items-center gap-2 pb-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.incluye_cargador}
                onChange={(e) => set({ incluye_cargador: e.target.checked })}
              />
              Incluye cargador
            </label>
            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {editingId != null ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                {editingId != null
                  ? saving
                    ? "Guardando…"
                    : "Guardar"
                  : saving
                    ? "Asignando…"
                    : "Asignar"}
              </button>
              {editingId != null && (
                <button
                  type="button"
                  onClick={reset}
                  className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
                >
                  <X className="w-4 h-4" /> Cancelar
                </button>
              )}
            </div>
          </div>
        </form>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {error}
        </div>
      )}
      {msg && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded p-3 text-sm">
          {msg}
        </div>
      )}

      {empleado && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">Tipo</th>
                <th className="px-3 py-2 font-medium">Marca</th>
                <th className="px-3 py-2 font-medium">Modelo</th>
                <th className="px-3 py-2 font-medium">No. de serie</th>
                <th className="px-3 py-2 font-medium">Características</th>
                <th className="px-3 py-2 font-medium">Cargador</th>
                <th className="px-3 py-2 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {equiposQ.isLoading && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                    Cargando…
                  </td>
                </tr>
              )}
              {!equiposQ.isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                    Este usuario aún no tiene equipos asignados.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t hover:bg-slate-50">
                  <td className="px-3 py-2">{r.tipo}</td>
                  <td className="px-3 py-2">{r.marca || "—"}</td>
                  <td className="px-3 py-2">{r.modelo || "—"}</td>
                  <td className="px-3 py-2 tabular-nums">{r.numero_serie || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{r.caracteristicas || "—"}</td>
                  <td className="px-3 py-2">{r.incluye_cargador ? "Sí" : "No"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => startEdit(r)}
                      className="text-slate-500 hover:text-blue-600 p-1"
                      title="Editar"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        setMsg(null);
                        setTransferPendiente(r);
                      }}
                      className="text-slate-500 hover:text-blue-600 p-1"
                      title="Transferir a otro usuario"
                    >
                      <ArrowRightLeft className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        setMsg(null);
                        setBajaPendiente(r);
                      }}
                      className="text-slate-500 hover:text-red-600 p-1"
                      title="Eliminar (baja lógica o física)"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {empleado && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-slate-100 border-b flex flex-wrap items-baseline justify-between gap-2">
            <div className="text-sm font-medium text-slate-700">
              Bajas de asignación{" "}
              <span className="font-normal text-slate-500">
                {esAdmin ? "(físicas y lógicas · vista de administrador)" : "(entrega física del equipo)"}
              </span>
            </div>
            <div className="text-xs text-slate-500">
              {esAdmin
                ? `Las físicas generan el formato PDF para firma; las lógicas solo quedan registradas.`
                : `Equipos que ${nombreSel} entregó. Genera el formato PDF para firma de quien entrega y quien recibe.`}
            </div>
          </div>
          {bajasQ.isError ? (
            <div className="px-3 py-4 text-sm text-amber-800 bg-amber-50">
              No se pudieron cargar las bajas: {(bajasQ.error as Error).message}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-slate-600 text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">Fecha de baja</th>
                  {esAdmin && <th className="px-3 py-2 font-medium">Baja</th>}
                  <th className="px-3 py-2 font-medium">Tipo</th>
                  <th className="px-3 py-2 font-medium">Marca</th>
                  <th className="px-3 py-2 font-medium">Modelo</th>
                  <th className="px-3 py-2 font-medium">No. de serie</th>
                  <th className="px-3 py-2 font-medium">Motivo / observaciones</th>
                  <th className="px-3 py-2 font-medium">Registró</th>
                  <th className="px-3 py-2 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {(bajasQ.isLoading || !meQ.isFetched) && (
                  <tr>
                    <td colSpan={esAdmin ? 9 : 8} className="px-3 py-6 text-center text-slate-500">
                      Cargando…
                    </td>
                  </tr>
                )}
                {meQ.isFetched && !bajasQ.isLoading && bajas.length === 0 && (
                  <tr>
                    <td colSpan={esAdmin ? 9 : 8} className="px-3 py-6 text-center text-slate-500">
                      {esAdmin
                        ? "Sin bajas registradas para este usuario."
                        : "Sin bajas físicas registradas para este usuario."}
                    </td>
                  </tr>
                )}
                {bajas.map((b) => (
                  <tr key={b.id} className="border-t hover:bg-slate-50">
                    <td className="px-3 py-2 tabular-nums whitespace-nowrap">
                      {fechaCorta(b.registrado_en)}
                    </td>
                    {esAdmin && (
                      <td className="px-3 py-2">
                        <span
                          className={`text-[11px] px-2 py-0.5 rounded-full ${
                            b.tipo_baja === "fisica"
                              ? "bg-red-100 text-red-700"
                              : "bg-slate-200 text-slate-700"
                          }`}
                          title={
                            b.tipo_baja === "fisica"
                              ? "Entrega física del equipo (genera PDF)"
                              : "Baja lógica: solo se quitó de la lista (no genera PDF)"
                          }
                        >
                          {b.tipo_baja === "fisica" ? "Física" : "Lógica"}
                        </span>
                      </td>
                    )}
                    <td className="px-3 py-2">{b.tipo}</td>
                    <td className="px-3 py-2">{b.marca || "—"}</td>
                    <td className="px-3 py-2">{b.modelo || "—"}</td>
                    <td className="px-3 py-2 tabular-nums">{b.numero_serie || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{b.motivo || "—"}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs">
                      {b.registrado_por.split("@")[0]}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {b.tipo_baja === "fisica" ? (
                        <button
                          type="button"
                          disabled={!!descargando}
                          onClick={() => generarFormatoBaja(b)}
                          className="inline-flex items-center gap-1.5 text-emerald-700 hover:text-emerald-900 p-1 text-xs font-medium disabled:opacity-50"
                          title="Descargar formato de baja de asignación (PDF) para firma"
                        >
                          <FileDown className="w-4 h-4" /> Formato PDF
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400 p-1" title="La baja lógica no genera PDF">
                          Sin PDF
                        </span>
                      )}
                      <button
                        type="button"
                        disabled={restaurar.isPending}
                        onClick={() => onRestaurar(b)}
                        className="text-slate-500 hover:text-blue-600 p-1 ml-1 disabled:opacity-50"
                        title="Restaurar: volver a asignar el equipo al usuario"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
